import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import { ApiError } from '@/api';
import { useNotify } from '@/components/feedback/notificationsContext';
import { downloadFile, importCsv } from '@/platform/api';
import type { ImportResult } from '@/platform/types';

export interface PreviewColumn {
  key: string;
  label: string;
  render?: (value: unknown, row: Record<string, unknown>) => ReactNode;
}

interface CsvImportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** API path, e.g. "/users/import". Called with ?dryRun=true first (preview), then false. */
  importPath: string;
  templatePath: string;
  templateName: string;
  /** The API also serves the template as Excel (templatePath?format=xlsx). */
  excelTemplate?: boolean;
  hints: ReactNode;
  previewColumns: PreviewColumn[];
  onImported: (result: ImportResult) => void;
}

const ACTION_LABEL: Record<string, { label: string; color: 'lime' | 'info' | 'default' }> = {
  create: { label: 'Novo', color: 'lime' },
  update: { label: 'Atualizar', color: 'info' },
  skip: { label: 'Ignorar', color: 'default' },
};

/**
 * Two-step import: upload → preview (nothing saved, errors per row) → confirm.
 * Accepts Excel workbooks (.xlsx) and CSV (`,` or `;` separated, UTF-8 or Windows-1252).
 */
export function CsvImportDialog({
  open,
  onClose,
  title,
  importPath,
  templatePath,
  templateName,
  excelTemplate = false,
  hints,
  previewColumns,
  onImported,
}: CsvImportDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const notify = useNotify();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError(null);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const analyze = async (picked: File) => {
    setFile(picked);
    setPreview(null);
    setError(null);
    setBusy(true);
    try {
      setPreview(await importCsv(importPath, picked, true));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível ler o arquivo.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const result = await importCsv(importPath, file, false);
      notify(
        `Importação concluída: ${result.created} novos, ${result.updated} atualizados.`,
        'success',
      );
      onImported(result);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao importar.');
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files[0];
    if (dropped) void analyze(dropped);
  };

  const rows = (preview?.preview ?? []).filter((r) => r.row !== 0);
  const extra = (preview?.preview ?? []).find((r) => r.action === 'categories') as
    { created: string[] } | undefined;
  const importable = (preview?.created ?? 0) + (preview?.updated ?? 0);

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
          >
            <Typography variant="body2" color="text.secondary">
              Use o modelo, já preenchido com exemplos, para acertar as colunas. Planilhas do Excel
              (.xlsx) e arquivos CSV funcionam direto.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {excelTemplate && (
                <Button
                  variant="tonal"
                  size="small"
                  startIcon={<DownloadRounded />}
                  onClick={() =>
                    void downloadFile(
                      `${templatePath}?format=xlsx`,
                      templateName.replace(/\.csv$/, '.xlsx'),
                    )
                  }
                >
                  Modelo Excel
                </Button>
              )}
              <Button
                variant="tonal"
                size="small"
                startIcon={<DownloadRounded />}
                onClick={() => void downloadFile(templatePath, templateName)}
              >
                {excelTemplate ? 'Modelo CSV' : 'Baixar modelo'}
              </Button>
            </Stack>
          </Stack>

          <Box
            role="button"
            tabIndex={0}
            onClick={() => input.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            sx={(t) => ({
              border: `2px dashed ${dragging ? t.vars.palette.primary.main : t.vars.palette.divider}`,
              bgcolor: dragging
                ? t.vars.palette.primary.container
                : t.vars.palette.background.subtle,
              borderRadius: 4,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 150ms',
              '&:hover, &:focus-visible': {
                borderColor: t.vars.palette.primary.main,
                outline: 'none',
              },
            })}
          >
            <UploadFileRounded color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="subtitle1">
              {file ? file.name : 'Arraste a planilha (.xlsx ou CSV) aqui ou clique para escolher'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Nada é salvo até você confirmar.
            </Typography>
            <input
              ref={input}
              type="file"
              accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => {
                const picked = e.target.files?.[0];
                e.target.value = '';
                if (picked) void analyze(picked);
              }}
            />
          </Box>

          {!preview && !busy && (
            <Alert severity="info" icon={false}>
              {hints}
            </Alert>
          )}
          {busy && !preview && (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <CircularProgress size={20} />
              <Typography>Analisando o arquivo…</Typography>
            </Stack>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          {preview && (
            <>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Chip variant="soft" label={`${preview.total} linhas`} />
                <Chip variant="soft" color="lime" label={`${preview.created} novos`} />
                <Chip variant="soft" color="info" label={`${preview.updated} atualizações`} />
                {preview.skipped > 0 && (
                  <Chip variant="soft" label={`${preview.skipped} ignorados (já existem)`} />
                )}
                {preview.errors.length > 0 && (
                  <Chip variant="soft" color="error" label={`${preview.errors.length} com erro`} />
                )}
              </Stack>
              {extra && extra.created.length > 0 && (
                <Alert severity="info">
                  Categorias que serão criadas: {extra.created.join(', ')}
                </Alert>
              )}
              {preview.errors.length > 0 && (
                <Alert severity="warning">
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Estas linhas serão puladas. Corrija o arquivo e envie de novo, se quiser.
                  </Typography>
                  <List dense disablePadding sx={{ maxHeight: 160, overflow: 'auto' }}>
                    {preview.errors.map((e) => (
                      <ListItem key={`${e.row}-${e.message}`} disableGutters sx={{ py: 0 }}>
                        <ListItemText
                          primary={`Linha ${e.row}: ${e.message}`}
                          secondary={e.content ? `Conteúdo da linha: ${e.content}` : undefined}
                          slotProps={{
                            primary: { variant: 'body2' },
                            secondary: { variant: 'caption', sx: { wordBreak: 'break-word' } },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}
              {rows.length > 0 && (
                <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 3 }}>
                  <Box
                    component="table"
                    sx={(t) => ({
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.875rem',
                      '& th, & td': {
                        textAlign: 'left',
                        px: 1.5,
                        py: 1,
                        borderBottom: `1px solid ${t.vars.palette.divider}`,
                        whiteSpace: 'nowrap',
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      '& th': {
                        color: 'text.secondary',
                        fontWeight: 600,
                        bgcolor: 'background.subtle',
                      },
                      '& tr:last-child td': { borderBottom: 0 },
                    })}
                  >
                    <thead>
                      <tr>
                        <th>Linha</th>
                        <th>Ação</th>
                        {previewColumns.map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const action = ACTION_LABEL[String(row.action)] ?? ACTION_LABEL.create;
                        return (
                          <tr key={String(row.row)}>
                            <td>{String(row.row)}</td>
                            <td>
                              <Chip
                                size="small"
                                variant="soft"
                                color={action.color}
                                label={action.label}
                              />
                            </td>
                            {previewColumns.map((c) => (
                              <td key={c.key} title={String(row[c.key] ?? '')}>
                                {c.render
                                  ? c.render(row[c.key], row)
                                  : Array.isArray(row[c.key])
                                    ? (row[c.key] as string[]).join(', ')
                                    : String(row[c.key] ?? '')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={close} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void confirm()}
          disabled={!preview || importable === 0 || busy}
          startIcon={busy && preview ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {preview
            ? `Importar ${importable} ${importable === 1 ? 'registro' : 'registros'}`
            : 'Importar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
